"use client";

import { useState, FormEvent } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

interface LeadFormSectionProps {
  defaultService?: string;
}

const neighborhoodOptions = [
  { value: "Select Neighborhood", labelKey: "form.selectNeighborhood" },
  { value: "Brickell", labelKey: null },
  { value: "Aventura", labelKey: null },
  { value: "Miami Beach", labelKey: null },
  { value: "Coral Gables", labelKey: null },
  { value: "Coconut Grove", labelKey: null },
  { value: "Doral", labelKey: null },
  { value: "Kendall", labelKey: null },
  { value: "Pinecrest", labelKey: null },
  { value: "Bay Harbor Islands", labelKey: null },
  { value: "Key Biscayne", labelKey: null },
  { value: "South Miami", labelKey: null },
  { value: "North Miami Beach", labelKey: null },
  { value: "Other Miami-Dade", labelKey: "form.neighborhoodOther" },
];

const serviceOptions = [
  { value: "sliding-door", labelKey: "home.servicesSliding" },
  { value: "glass", labelKey: "form.optGlass" },
  { value: "hardware", labelKey: "home.servicesHardware" },
  { value: "weather", labelKey: "home.servicesWeather" },
  { value: "maintenance", labelKey: "form.optMaint" },
  { value: "boardup", labelKey: "home.servicesBoardup" },
  { value: "not-sure", labelKey: "form.optNotSure" },
];

export default function LeadFormSection({ defaultService = "" }: LeadFormSectionProps) {
  const { t, currentLang } = useLanguage();
  const isEs = currentLang === "es";
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [neighborhood, setNeighborhood] = useState("Select Neighborhood");
  const [service, setService] = useState(defaultService);
  const [gotcha, setGotcha] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (gotcha.trim() !== "") return;

    setStatus("submitting");

    const selectedOpt = serviceOptions.find((o) => o.value === service);
    const serviceLabel = selectedOpt ? t(selectedOpt.labelKey) : "";

    const endpoint = "https://formspree.io/f/YOUR-FORM-ID-HERE";

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ name, phone, neighborhood, service, serviceLabel }),
      });

      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <section className="bg-[#fefce8] border-t-[3px] border-[#047857] py-12 md:py-[50px]">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="bg-[#fefce8] p-8 rounded-xl border-l-4 border-[#047857] max-w-2xl mx-auto">
            <div className="flex flex-col items-center text-center gap-3">
              <i className="ri-checkbox-circle-fill text-[32px] text-[#047857]"></i>
              <h3 className="text-[#064e3b] text-xl font-bold">{t("form.successTitle")}</h3>
              <p className="text-sm text-gray-600">
                {t("form.successText")}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#fefce8] border-t-[3px] border-[#047857] py-12 md:py-[50px]">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="text-center mb-6">
          <h2 className="text-[#064e3b] text-[28px] font-bold mb-2">
            {t("form.heading")}
          </h2>
          <p className="text-gray-500 text-base max-w-[720px] mx-auto">
            {t("form.subhead")}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 md:flex-row md:items-center md:gap-3"
          data-readdy-form="lead-quote"
        >
          {/* Honeypot — anti-spam, invisible to users */}
          <input
            type="text"
            name="_gotcha"
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={gotcha}
            onChange={(e) => setGotcha(e.target.value)}
          />

          {/* Name */}
          <div className="flex-[1.2]">
            <label htmlFor="lead-name" className="sr-only">{t("form.nameLabel")}</label>
            <input
              id="lead-name"
              type="text"
              name="name"
              required
              aria-required="true"
              placeholder={t("form.nameLabel") as string}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3.5 border border-gray-300 rounded-lg bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#047857]"
            />
          </div>

          {/* Phone */}
          <div className="flex-1">
            <label htmlFor="lead-phone" className="sr-only">{t("form.phoneLabel")}</label>
            <input
              id="lead-phone"
              type="tel"
              name="phone"
              required
              aria-required="true"
              placeholder={t("form.phoneLabel") as string}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3.5 border border-gray-300 rounded-lg bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#047857]"
            />
          </div>

          {/* Neighborhood */}
          <div className="flex-1 relative">
            <label htmlFor="lead-neighborhood" className="sr-only">{t("form.neighborhoodLabel")}</label>
            <select
              id="lead-neighborhood"
              name="neighborhood"
              required
              aria-required="true"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              className="w-full px-4 py-3.5 border border-gray-300 rounded-lg bg-white text-[15px] appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#047857]"
            >
              {neighborhoodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.labelKey ? t(opt.labelKey) : opt.value}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
          </div>

          {/* Service */}
          <div className="flex-1 relative">
            <label htmlFor="lead-service" className="sr-only">{t("form.serviceLabel")}</label>
            <select
              id="lead-service"
              name="service"
              required
              aria-required="true"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full px-4 py-3.5 border border-gray-300 rounded-lg bg-white text-[15px] appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-[#047857]"
            >
              <option value="">{t("form.selectService")}</option>
              {serviceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
          </div>

          {/* Submit */}
          <div className="md:flex-[0.8]">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full md:w-auto whitespace-nowrap px-7 py-3.5 bg-[#ea580c] text-white font-semibold text-[15px] uppercase tracking-[0.5px] rounded-lg cursor-pointer hover:bg-[#c2410c] disabled:opacity-60 transition-colors"
            >
              {status === "submitting" ? t("form.sending") : t("form.submit")}
            </button>
          </div>
        </form>

        {/* Error message */}
        {status === "error" && (
          <p className="mt-3 text-sm text-red-600 text-center" role="alert">
            {t("form.errorText")}
          </p>
        )}

        {/* Trust badges */}
        <div className="mt-5 flex flex-wrap justify-center gap-3 md:gap-6">
          {[
            { key: "form.badge1", icon: "ri-check-line" },
            { key: "form.badge2", icon: "ri-check-line" },
            { key: "form.badge3", icon: "ri-check-line" },
          ].map((badge) => (
            <span key={badge.key} className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 bg-white/60 px-3 py-1.5 rounded-full">
              <i className={`${badge.icon} text-[#047857]`}></i>
              {t(badge.key)}
            </span>
          ))}
        </div>

        {/* TCPA compliance disclaimer */}
        <p className="mt-3 text-xs text-gray-500 text-center max-w-[700px] mx-auto">
          {isEs
            ? "Al enviar este formulario, acepta recibir llamadas y mensajes de texto de nuestro equipo sobre su consulta. Pueden aplicar tarifas estándar de mensajes y datos. Responda STOP para cancelar."
            : "By submitting this form, you consent to receive calls and text messages from our team regarding your inquiry. Standard message and data rates may apply. Reply STOP to opt out."}
        </p>

        {/* Disclaimer */}
        <p className="mt-3 text-xs italic text-gray-500 text-center max-w-[900px] mx-auto">
          {t("form.disclaimer")}
        </p>
      </div>
    </section>
  );
}